<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TicketMessageList from './TicketMessageList.vue';
import TicketReplyBox from './TicketReplyBox.vue';

const props = defineProps({
  ticket: { type: Object, default: () => ({}) },
  isOnExpandedLayout: { type: Boolean, default: false },
});

const { t } = useI18n();
const store = useStore();

const isReopening = ref(false);

const hasTicket = computed(() => Boolean(props.ticket?.id));

const ticketStatus = computed(() => props.ticket?.status || 'open');

const isReplyDisabled = computed(
  () => ticketStatus.value === 'resolved' || ticketStatus.value === 'closed'
);

const showReopenButton = computed(() => ticketStatus.value === 'resolved');

const replyPlaceholder = computed(() => {
  if (ticketStatus.value === 'closed') {
    return t('SUPPORT_TICKETS.REPLY_DISABLED_CLOSED');
  }
  if (ticketStatus.value === 'resolved') {
    return t('SUPPORT_TICKETS.REPLY_DISABLED_RESOLVED');
  }
  return '';
});

const reopenTicket = async () => {
  if (isReopening.value || !props.ticket?.id) return;

  isReopening.value = true;
  try {
    await store.dispatch('supportTickets/reopenSupportTicket', props.ticket.id);
  } finally {
    isReopening.value = false;
  }
};

const statusLabel = computed(() => {
  const statusKey = (props.ticket?.status || 'open')
    .replace(/-/g, '_')
    .toUpperCase();
  return t(`SUPPORT_TICKETS.STATUS_OPTIONS.${statusKey}`);
});

const priorityLabel = computed(() => {
  const priorityKey = (props.ticket?.priority || 'medium').toUpperCase();
  return t(`SUPPORT_TICKETS.PRIORITY_OPTIONS.${priorityKey}`);
});
</script>

<template>
  <div
    class="flex flex-col flex-1 min-w-0 min-h-0 h-full bg-n-surface-1"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <template v-if="hasTicket">
      <header class="px-4 py-3 border-b border-n-weak shrink-0">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0 flex-1">
            <p class="text-xs font-medium text-n-slate-10">
              {{ t('SUPPORT_TICKETS.TICKET_ID', { id: ticket.displayId }) }}
            </p>
            <h2 class="mt-1 text-base font-semibold text-n-slate-12">
              {{ ticket.subject }}
            </h2>
            <p class="mt-2 text-xs text-n-slate-11">
              {{ t('SUPPORT_TICKETS.PLATFORM_NOTE') }}
            </p>
            <div
              v-if="ticket.meta?.creator"
              class="flex items-center gap-2 mt-2"
            >
              <Avatar
                :name="ticket.meta.creator.name"
                :src="ticket.meta.creator.thumbnail"
                :size="24"
                rounded-full
              />
              <span class="text-xs text-n-slate-11">
                {{ t('SUPPORT_TICKETS.CREATOR') }}:
                {{ ticket.meta.creator.name }}
              </span>
            </div>
          </div>
          <NextButton
            v-if="showReopenButton"
            icon="i-lucide-rotate-ccw"
            :label="t('SUPPORT_TICKETS.REOPEN')"
            :is-loading="isReopening"
            :disabled="isReopening"
            @click="reopenTicket"
          />
        </div>
        <div class="flex flex-wrap gap-2 mt-4">
          <span
            class="px-2 py-1 text-xs font-medium rounded-md bg-n-alpha-2 text-n-slate-12"
          >
            {{ t('SUPPORT_TICKETS.STATUS') }}: {{ statusLabel }}
          </span>
          <span
            class="px-2 py-1 text-xs font-medium rounded-md bg-n-alpha-2 text-n-slate-12"
          >
            {{ t('SUPPORT_TICKETS.PRIORITY') }}: {{ priorityLabel }}
          </span>
        </div>
      </header>
      <TicketMessageList :messages="ticket.messages || []" />
      <TicketReplyBox
        :ticket-id="ticket.id"
        :disabled="isReplyDisabled"
        :placeholder="replyPlaceholder"
      />
    </template>
    <div
      v-else
      class="flex flex-col items-center justify-center flex-1 gap-2 px-6 text-center"
    >
      <span class="text-4xl i-lucide-ticket text-n-slate-9" />
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ t('SUPPORT_TICKETS.EMPTY_STATE.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('SUPPORT_TICKETS.EMPTY_STATE.SUBTITLE') }}
      </p>
    </div>
  </div>
</template>
